/**
 * Design DNA Engine — the identity of a generated template.
 *
 * DNA is the complete set of design genes that make a template what it is.
 * Two templates with the same DNA are the same design; similarity between
 * DNA records (see duplicateDetectionEngine) is how the factory guarantees
 * it never manufactures near-clones.
 *
 * heroPosition and ctaPosition are derived from the layout gene rather than
 * drawn independently — the layout preset is what physically places those
 * areas, so letting them vary independently would create DNA that lies.
 */

/** Gene names, in fingerprint order. Order is part of the contract. */
export const GENES = [
  'layout', 'heroPosition', 'ctaPosition',
  'paletteTheme', 'harmony', 'seed', 'dark',
  'typographyTheme', 'typeScale',
  'spacing', 'decorationPack', 'shapeStyle',
  'offerStyle', 'footerStyle',
  'imageCount', 'imageStyle',
];

/** Where each layout preset physically puts its hero and CTA. */
export const LAYOUT_POSITIONS = {
  'classic-flyer': { heroPosition: 'top', ctaPosition: 'lower-third' },
  'split-hero': { heroPosition: 'left', ctaPosition: 'left-column' },
  'centered-poster': { heroPosition: 'center', ctaPosition: 'lower-third' },
  'offer-first': { heroPosition: 'middle', ctaPosition: 'upper-third' },
};

export const OFFER_STYLES = ['badge', 'price-circle', 'ribbon-text'];
export const FOOTER_STYLES = ['contact', 'social', 'minimal'];
export const IMAGE_STYLES = ['photo', 'illustration', 'flat', 'studio'];

/**
 * Assemble a DNA record from raw genes, deriving the position genes.
 * Throws on missing genes so half-formed DNA can't enter the pipeline.
 */
export const createDNA = (genes) => {
  const positions = LAYOUT_POSITIONS[genes.layout];
  if (!positions) throw new Error(`createDNA: unknown layout gene "${genes.layout}"`);
  const dna = { ...genes, ...positions };
  const missing = GENES.filter((g) => dna[g] === undefined || dna[g] === null);
  if (missing.length) throw new Error(`createDNA: missing gene(s) ${missing.join(', ')}`);
  return dna;
};

/** Stable string identity — same DNA, same fingerprint, always. */
export const fingerprint = (dna) => GENES.map((g) => `${g}:${dna[g]}`).join('|');

/** Hue (0–360) of a hex color — used for perceptual seed comparison. */
export const hexHue = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return 0;
  const r = parseInt(m[1], 16) / 255, g = parseInt(m[2], 16) / 255, b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  if (max === min) return 0;
  const d = max - min;
  let h;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return h * 360;
};
